import { CONTRIBUTOR_ROLE_ID } from "./rbac.js";

/**
 * Wire the deployed gateway Container App so it can send the bi-weekly cost
 * report directly via ACS Email:
 *
 *  1. Grant the gateway's managed identity Contributor at the ACS resource
 *     scope — this authorises the ACS Email data-plane send (mirrors the
 *     executor MI's Contributor grant that provisionAcsEmail relies on).
 *  2. Set the three cost-reporter env vars on the gateway Container App
 *     (endpoint, sender, and the M8T_ENABLE_COST_REPORTER opt-in flag).
 *
 * Idempotent: `az role assignment create` is a no-op on a duplicate
 * (RoleAssignmentExists), and `--set-env-vars` is declarative, so this is
 * safely re-runnable. The `az` runner is injected so it can be faked in tests
 * (mirrors acr.ts/grantAcrPullViaAz).
 */
export async function wireGatewayForAcs(args: {
  az: (a: string[]) => Promise<string>;
  resourceGroup: string;
  acsResourceId: string;
  gatewayApp: string;
  endpoint: string;
  sender: string;
  gatewayPrincipalId: string;
}): Promise<void> {
  try {
    await args.az([
      "role", "assignment", "create",
      "--assignee-object-id", args.gatewayPrincipalId,
      "--assignee-principal-type", "ServicePrincipal",
      "--role", CONTRIBUTOR_ROLE_ID,
      "--scope", args.acsResourceId,
    ]);
  } catch (e) {
    // az role assignment create emits RoleAssignmentExists on a duplicate; swallow
    // ONLY that (a broad /exists/i match would hide ResourceGroupNotFound / auth errors).
    if (!(e instanceof Error && /RoleAssignmentExists/i.test(e.message))) throw e;
  }

  await args.az([
    "containerapp", "update",
    "-n", args.gatewayApp,
    "-g", args.resourceGroup,
    "--set-env-vars",
    `M8T_ACS_ENDPOINT=${args.endpoint}`,
    `M8T_ACS_SENDER=${args.sender}`,
    "M8T_ENABLE_COST_REPORTER=1",
  ]);
}
